import type { RoleTemplate } from './types.js';

/**
 * The fallback for a role that is not in the library and cannot be drafted by AI right now: a sound,
 * generic interview and assessment the recruiter then edits. The AI draft replaces it when it can.
 */
export const generalRole: RoleTemplate = {
  id: 'general',
  title: 'General role',
  category: 'admin',
  department: 'General',
  summary: 'A generic starting point for any role: communication, job knowledge, problem solving and ownership.',
  aliases: [],
  customerFacing: false,
  shifts: false,
  experience: { min: 0, max: 10 },
  relevantExperience: 'a similar role',
  description:
    '{company} is hiring for this role. The person in it owns their day-to-day work, works closely with colleagues and managers, and is expected to communicate clearly, solve problems on their own and keep their commitments.',
  competencies: [
    {
      key: 'communication',
      label: 'Communication',
      description:
        'Explains clearly and in a structured way, listens to the question actually asked, and adapts to the listener. Assessed across the whole conversation, not from one answer.',
      weight: 25,
    },
    {
      key: 'job_knowledge',
      label: 'Job knowledge and skills',
      description:
        'Has real, specific experience with the core work of this role and the skills, tools or qualifications the hiring team asked for. Specific examples score higher than general claims.',
      probe: 'Walk me through a typical day in your current or last job. Which part of that work are you strongest at, and how do you know?',
      weight: 30,
    },
    {
      key: 'problem_solving',
      label: 'Problem solving',
      description: 'Finds the real cause of a problem, weighs options, acts, and learns from the result; knows when to escalate and does it with complete information.',
      probe: 'Tell me about a problem at work that you solved yourself. What was wrong, what did you try, and what happened?',
      weight: 20,
    },
    {
      key: 'ownership',
      label: 'Ownership and teamwork',
      description: 'Keeps commitments, flags risks early, helps colleagues, and takes responsibility for mistakes instead of blaming others.',
      probe: 'Tell me about a time you could not meet a deadline you had promised. What did you do, and when did you tell people?',
      weight: 15,
    },
    {
      key: 'motivation_fit',
      label: 'Motivation and role fit',
      description: 'Why this role and why now; reasons for leaving the current job; comfort with the work pattern; likely stability in the role.',
      weight: 10,
    },
  ],
  facts: [],
  knockouts: [],
  tasks: [
    {
      kind: 'roleplay',
      id: 'manager_missed_deadline',
      title: 'Your manager asks about a missed deadline',
      brief: 'You work at {company}. Your manager, {customer}, calls you: a piece of work you promised for today is not ready.',
      persona: 'A busy, direct manager; not angry yet, but under pressure from their own boss and wants a straight answer.',
      situation:
        'The work is about 70% done. It was delayed because another team sent the information two days late, which the candidate never told the manager. The manager has a review with their boss tomorrow at 10 am.',
      twist: 'The manager asks why they are only hearing about the delay now.',
      goodResolution:
        'Gives an honest status without excuses, explains the dependency briefly, owns not flagging it earlier, proposes a concrete plan (what will be ready by 10 am tomorrow, what help is needed), and agrees how they will update the manager.',
      skills: ['ownership', 'communication'],
      minutes: 4,
    },
    {
      kind: 'roleplay',
      id: 'colleague_urgent_help',
      title: 'A colleague needs help with an urgent problem',
      brief: '{customer}, a colleague at {company}, comes to you with an urgent problem while you are busy with your own work.',
      persona: 'A stressed colleague who explains the problem in a rush and jumps between details.',
      situation:
        'A report for an important customer, {org}, shows totals that do not match last month. The real cause is that last month\'s figures were updated after the report was sent; nothing is broken now. The colleague has not checked when the figures changed.',
      twist: 'The colleague asks you to just send the report as it is so the problem goes away.',
      goodResolution:
        'Stays calm, asks focused questions to find the real cause, suggests checking when the figures changed, does not send anything misleading, and agrees on who does what next and by when.',
      skills: ['problem_solving', 'communication', 'ownership'],
      minutes: 4,
    },
    {
      kind: 'roleplay',
      id: 'unhappy_stakeholder',
      title: 'An unhappy customer or stakeholder',
      brief: '{customer} from {org} calls you, unhappy that something {company} promised them has not happened.',
      persona: 'A frustrated customer who feels ignored; polite but firm, wants a date and a name.',
      situation:
        'A document they were promised last week is waiting for one approval inside {company}. Nobody told them. The approver is on leave until Monday; a colleague can approve in their absence if asked.',
      twist: 'They say they will escalate to your senior management today.',
      goodResolution:
        'Apologises and acknowledges the missed update, finds out exactly what is pending, proposes getting the backup approver today, gives a realistic date and a follow-up time, and stays calm about the escalation.',
      skills: ['communication', 'problem_solving', 'ownership'],
      minutes: 4,
    },
    {
      kind: 'written',
      id: 'delay_email_manager',
      title: 'Email your manager about a delay',
      brief:
        'You are working on a task due Friday for {org}, an important customer of {company}. On Wednesday you learn that a supplier will deliver the information you need on Monday, not Tuesday as planned. Without it you can finish about 80% of the work by Friday.\n\nWrite the email to your manager.',
      goodAnswer:
        'States the problem and its impact in the first lines, says what will and will not be ready on Friday, offers options (partial delivery, asking the supplier for part of the data, a new date), recommends one, and says what they need from the manager. Short, clear and professional.',
      skills: ['communication', 'ownership'],
      minutes: 5,
    },
    {
      kind: 'written',
      id: 'prioritise_day',
      title: 'Plan a crowded day',
      brief:
        'It is 9 am. You have: (a) a report for your manager due at 12 noon, (b) a customer who emailed yesterday about a wrong invoice, (c) a team meeting from 11 to 11:30, (d) a request from another department that they call "urgent" but is due Friday, (e) 20 unread emails.\n\nWrite how you would plan the morning and why, in a few lines.',
      goodAnswer:
        'Orders the work sensibly (for example: quick reply to the customer with a time, the report before noon around the meeting, a short scan of emails for anything truly urgent, the Friday request planned for later), explains the reasoning, and mentions telling people what to expect.',
      skills: ['problem_solving', 'ownership'],
      minutes: 4,
    },
  ],
};
